import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Nav = styled.nav`
  background: ${theme.colors.black};
  color: ${theme.colors.white};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 2.5rem;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  z-index: 101;

  @media (max-width: 600px) {
    gap: 0.5rem;
  }
`;

const LogoImage = styled.img`
  height: 36px;
  width: auto;

  @media (max-width: 600px) {
    height: 28px;
  }
`;

const LogoText = styled.span`
  font-size: 2.1rem;
  font-family: ${theme.font.heading};
  font-weight: bold;
  color: ${theme.colors.green};
  letter-spacing: -2px;
  user-select: none;

  @media (max-width: 600px) {
    font-size: 1.4rem;
    letter-spacing: -1px;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2.2rem;
  align-items: center;
  flex-wrap: wrap;

  a {
    color: ${theme.colors.white};
    text-decoration: none;
    font-weight: 500;
    font-size: 1.08rem;
    position: relative;
    padding-bottom: 2px;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${theme.colors.green};
    }
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const MobileNavContainer = styled.div<{ isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 1.5rem;
  
  position: fixed;
  top: 0;
  right: 0;
  width: min(80vw, 320px);
  height: 100vh;
  
  background: ${theme.colors.black};
  padding: 5rem 2rem 2rem;
  z-index: 100;
  
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(100%)')};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: transform 0.3s ease-in-out, visibility 0.3s ease-in-out;
  box-shadow: ${({ isOpen }) => (isOpen ? '-5px 0 15px rgba(0, 0, 0, 0.3)' : 'none')};

  a {
    font-size: 1.2rem;
    padding: 0.75rem 0;
    width: 100%;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: ${theme.colors.white};
    text-decoration: none;
    font-weight: 500;
      
    &:last-of-type {
      border-bottom: none;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  width: 2.5rem;
  height: 2.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 101;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 2rem;
    height: 3px;
    background-color: ${theme.colors.white};
    border-radius: 2px;
    transition: background-color 0.2s ease;
  }

  &::before {
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }

  &:hover::before,
  &:hover::after {
    background-color: ${theme.colors.green};
  }
`;

const MobileLanguageSwitcherContainer = styled.div`
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
  display: flex;
  justify-content: center;

  /* Override LanguageSwitcher button styles on mobile */
  button {
    background: ${theme.colors.black};
    color: ${theme.colors.white};
    border-color: rgba(255, 255, 255, 0.3);

    /* Style for the active button */
    &[data-active='true'] {
      background: ${theme.colors.green};
      color: ${theme.colors.black};
      border-color: ${theme.colors.green};
    }
  }
`;

const HamburgerButton = styled.button<{ isOpen: boolean }>`
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 101;

  @media (max-width: 900px) {
    display: flex;
  }

  div {
    width: 2rem;
    height: 0.25rem;
    background: ${theme.colors.white};
    border-radius: 10px;
    transition: all 0.3s linear;
    position: relative;
    transform-origin: 1px;

    &:first-child {
      transform: ${({ isOpen }) => (isOpen ? 'rotate(45deg)' : 'rotate(0)')};
    }

    &:nth-child(2) {
      opacity: ${({ isOpen }) => (isOpen ? '0' : '1')};
      transform: ${({ isOpen }) => (isOpen ? 'translateX(20px)' : 'translateX(0)')};
    }

    &:nth-child(3) {
      transform: ${({ isOpen }) => (isOpen ? 'rotate(-45deg)' : 'rotate(0)')};
    }
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  display: none;
  
  @media (max-width: 900px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99;
    backdrop-filter: blur(2px);
  }
`;

const Navbar: React.FC = () => {
  const { language } = useLanguage();
  const t = translations.menu[language];
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Close menu when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Close menu on mobile when clicking a nav link
    if (window.innerWidth <= 900) {
      closeMenu();
    }
  };

  return (
    <>
      <Nav>
        <LogoContainer onClick={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          closeMenu();
        }}>
          <LogoImage src="/logo.png" alt="LoxPro Logo" />
          <LogoText>LoxPro</LogoText>
        </LogoContainer>
        
        <HamburgerButton isOpen={isMenuOpen} onClick={toggleMenu} aria-label="Toggle menu">
          <div />
          <div />
          <div />
        </HamburgerButton>

        {/* Desktop Navigation */}
        <NavLinks>
          <a href="#home">{t.home}</a>
          <a href="#about">{t.about}</a>
          <a href="#benefits">{t.benefits}</a>
          <a href="#gallery">{t.gallery}</a>
          <a href="#contact">{t.contact}</a>
          <LanguageSwitcher />
        </NavLinks>
      </Nav>
      
      {/* Mobile Navigation */}
      <MobileNavContainer isOpen={isMenuOpen}>
        <CloseButton onClick={closeMenu} aria-label="Close Menu" />
        <a href="#home" onClick={handleNavClick}>{t.home}</a>
        <a href="#about" onClick={handleNavClick}>{t.about}</a>
        <a href="#benefits" onClick={handleNavClick}>{t.benefits}</a>
        <a href="#gallery" onClick={handleNavClick}>{t.gallery}</a>
        <a href="#contact" onClick={handleNavClick}>{t.contact}</a>
        <MobileLanguageSwitcherContainer>
          <LanguageSwitcher />
        </MobileLanguageSwitcherContainer>
      </MobileNavContainer>

      <Overlay isOpen={isMenuOpen} onClick={closeMenu} />
    </>
  );
};

export default Navbar;
